import React from "react";
import { useSelector } from "react-redux";

import RequestDetails from "./RequestDetails";
import LabelAndValue from "./LabelAndValue";

import { batterySwapSteps } from "../constants";
import WeightDeliveredIcon from "../../../../Common/Svgs/WeightDeliveredIcon";
import WeightCollectedIcon from "../../../../Common/Svgs/WeightCollectedIcon";
import { formatDateTimeWithToday } from "../../../../Utils/formatDate";

const AssignmentSuccess = ({ step }) => {
  const { activeBatterySosRequest } = useSelector((state) => state.trips);
  const isAgentFlow =
    step === batterySwapSteps.REQUEST_ACCEPTED_AGENT ||
    step === batterySwapSteps.REQUEST_COMPLETED_AGENT;

  const isCompleted = step === batterySwapSteps.REQUEST_COMPLETED_AGENT;

  const isCompletedSwap = activeBatterySosRequest?.status === 5;
  const isAgentArrived =
    activeBatterySosRequest?.status === 4 ||
    activeBatterySosRequest?.status === 5;

  if (!activeBatterySosRequest) return null;

  const request = activeBatterySosRequest;
  const acceptedByName = request.acceptedByName || "Admin";
  const assignedTime = request.assignedAgentAt || request.updatedAt;

  return (
    <>
      <RequestDetails step={step} />

      <div className="mt-3 bg-primary-10 rounded-xl py-4 px-6">
        <div
          className="
            inline-flex
            items-center
            px-2
            py-1
            rounded-md
            border
            border-primary
            bg-white
          "
        >
          <span className="font-poppins font-medium text-xs text-primary leading-none">
            {isCompleted ? "Completed" : "Accepted"}
          </span>
        </div>

        <p className="mt-3 font-poppins font-medium text-sm text-black leading-none">
          {isAgentFlow
            ? "Agent & Batteries are assigned for this request"
            : "Batteries are assigned for this request"}
        </p>

        <div className="mt-3 flex">
          <LabelAndValue value={acceptedByName} label="Accepted By" />

          <LabelAndValue
            value={formatDateTimeWithToday(assignedTime)}
            label="Date & Time"
          />
        </div>

        <div className="mt-3 space-y-3">
          <AssignmentTypeCard isAgentFlow={isAgentFlow} />

          {isAgentFlow && (
            <AgentCard
              isCompleted={isAgentArrived}
              agentName={request.agentName}
              arrivalTime={
                request.agentArrivedAt ||
                request.agent_arrived_at ||
                request.arrivalTime
              }
            />
          )}
          <BatteryCard
            isCompleted={isCompletedSwap}
            batteryCount={request.outBatteries?.length || request.request?.batteryCount || 2}
            completedTime={request.completedAt || request.completed_at}
          />
        </div>
      </div>
    </>
  );
};

const AssignmentTypeCard = ({ isAgentFlow }) => {
  return (
    <div
      className="
        bg-white
        rounded-md
        p-4
        flex
        items-center
        justify-between
      "
    >
      <div className="flex items-center gap-2">
        <div
          className="
            w-10
            h-10
            rounded-full
            bg-primary-10
            flex
            items-center
            justify-center
          "
        >
          {isAgentFlow ? (
            <WeightDeliveredIcon className="text-primary" size={16} />
          ) : (
            <WeightCollectedIcon className="text-primary" size={16} />
          )}
        </div>

        <span className="font-poppins font-medium text-base leading-none text-black">
          {isAgentFlow ? "Deliver Via Agent" : "Pickup at Hub Station"}
        </span>
      </div>
    </div>
  );
};

const AgentCard = ({ isCompleted, agentName, arrivalTime }) => {
  const timeStr = formatDateTimeWithToday(arrivalTime);
  return (
    <div
      className="
        bg-white
        rounded-md
        p-4
        flex
        items-center
        justify-between
      "
    >
      <div className="flex items-center gap-2">
        <div
          className="
            w-10
            h-10
            rounded-full
            bg-primary-10
            flex
            items-center
            justify-center
          "
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M8.10573 7.24634C8.03906 7.23967 7.95906 7.23967 7.88573 7.24634C6.29906 7.19301 5.03906 5.89301 5.03906 4.29301C5.03906 2.65967 6.35906 1.33301 7.99906 1.33301C9.6324 1.33301 10.9591 2.65967 10.9591 4.29301C10.9524 5.89301 9.6924 7.19301 8.10573 7.24634Z"
              stroke="#085054"
              strokeWidth="1.5"
            />
            <path
              d="M4.7725 9.70699C3.15917 10.787 3.15917 12.547 4.7725 13.6203C6.60583 14.847 9.6125 14.847 11.4458 13.6203C13.0592 12.5403 13.0592 10.7803 11.4458 9.70699C9.61917 8.48699 6.6125 8.48699 4.7725 9.70699Z"
              stroke="#085054"
              strokeWidth="1.5"
            />
          </svg>
        </div>

        {isCompleted ? (
          <LabelAndValue
            value={agentName || "Technical Agent"}
            label={`Arrival time: ${timeStr}`}
          />
        ) : (
          <span className="font-poppins font-medium text-base text-black leading-none">
            {agentName || "Technical Agent"}
          </span>
        )}
      </div>

      <StatusBadge status={isCompleted ? "Completed" : "Assigned"} />
    </div>
  );
};

const BatteryCard = ({ isCompleted, batteryCount, completedTime }) => {
  const swapTimeStr = formatDateTimeWithToday(completedTime);

  return (
    <div
      className="
        bg-white
        rounded-lg
        px-4
        py-4
        flex
        items-center
        justify-between
      "
    >
      <div className="flex items-center gap-3">
        <div
          className="
            w-10
            h-10
            rounded-full
            bg-yellow-10
            flex
            items-center
            justify-center
          "
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13.668 6.33301C14.668 6.33301 14.668 6.66634 14.668 7.33301V8.66634C14.668 9.33301 14.668 9.66634 13.668 9.66634"
              stroke="#EAB726"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.66536 12.6663H4.66536C1.9987 12.6663 1.33203 11.9997 1.33203 9.33301V6.66634C1.33203 3.99967 1.9987 3.33301 4.66536 3.33301H8.66536C11.332 3.33301 11.9987 3.99967 11.9987 6.66634V9.33301C11.9987 11.9997 11.332 12.6663 8.66536 12.6663Z"
              stroke="#EAB726"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M4.25391 6.66699C4.47391 7.54033 4.47391 8.46033 4.25391 9.33366"
              stroke="#EAB726"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.58594 6.66699C6.80594 7.54033 6.80594 8.46033 6.58594 9.33366"
              stroke="#EAB726"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M8.92188 6.66699C9.14188 7.54033 9.14188 8.46033 8.92188 9.33366"
              stroke="#EAB726"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>{" "}
        </div>

        {isCompleted ? (
          <LabelAndValue
            value={`${batteryCount} Batteries`}
            label={`Swap time: ${swapTimeStr}`}
          />
        ) : (
          <span className="font-poppins font-medium text-base text-black leading-none">
            {batteryCount} Batteries
          </span>
        )}
      </div>

      <StatusBadge status={isCompleted ? "Completed" : "Assigned"} />
    </div>
  );
};

const StatusBadge = ({ status }) => {
  return (
    <div
      className="
        bg-primary-10
        rounded-md
        px-2
        py-1
        flex
        items-center
        gap-2
      "
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
        <path
          d="M2.66797 8.00033L6.21934 11.3337L13.3346 4.66699"
          stroke="#085054"
          strokeWidth="1.5"
        />
      </svg>

      <span className="font-poppins font-medium text-xs leading-none text-primary">
        {status}
      </span>
    </div>
  );
};

export default AssignmentSuccess;
